import { cva, type VariantProps } from "class-variance-authority";
import { type FC, type Ref, type SVGAttributes } from "react";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";

export const SPARKLINE_TONES = ["accent", "confirm", "danger", "neutral"] as const;
export type SparklineTone = (typeof SPARKLINE_TONES)[number];

const sparklineVariants = cva("w-full", {
	variants: {
		tone: {
			accent: "text-brand-accent",
			confirm: "text-confirm",
			danger: "text-danger",
			neutral: "text-tertiary",
		},
	},
	defaultVariants: {
		tone: "accent",
	},
});

const VIEWBOX_WIDTH = 120;
const VIEWBOX_HEIGHT = 28;
const VERTICAL_PADDING = 3;

const round = (n: number): number => Math.round(n * 100) / 100;

/** Normalizes y to the series' own min/max. A flat series (and the min of any varying one) rests on the bottom baseline so sibling sparklines stay aligned. */
const buildPolylinePoints = (series: number[]): string => {
	const min = Math.min(...series);
	const max = Math.max(...series);
	const range = max - min;
	const stepX = VIEWBOX_WIDTH / (series.length - 1);
	const usableHeight = VIEWBOX_HEIGHT - VERTICAL_PADDING * 2;

	return series
		.map((point, i) => {
			const x = i * stepX;
			const norm = range === 0 ? 0 : (point - min) / range;
			const y = VIEWBOX_HEIGHT - VERTICAL_PADDING - norm * usableHeight;
			return `${round(x)},${round(y)}`;
		})
		.join(" ");
};

export type SparklineProps = Omit<SVGAttributes<SVGSVGElement>, "children" | "points"> &
	VariantProps<typeof sparklineVariants> & {
		/** Ordered data points, oldest → newest. Renders nothing with fewer than two points. */
		points: number[];
		trackingId?: string;
		ref?: Ref<SVGSVGElement>;
	};

/** Stroke uses `currentColor`, so a shared `tone` keeps it in sync with an adjacent badge. */
export const Sparkline: FC<SparklineProps> = ({
	points,
	tone,
	trackingId,
	className,
	ref,
	...rest
}) => {
	if (points.length < 2) return null;

	return (
		<svg
			ref={ref}
			viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
			preserveAspectRatio="none"
			className={cn(sparklineVariants({ tone }), className)}
			{...trackingAttrs(trackingId)}
			{...rest}
		>
			<polyline
				points={buildPolylinePoints(points)}
				fill="none"
				stroke="currentColor"
				strokeWidth={2}
				strokeLinecap="round"
				strokeLinejoin="round"
				vectorEffect="non-scaling-stroke"
			/>
		</svg>
	);
};
Sparkline.displayName = "Sparkline";
